import { useEffect } from 'react'
import cx from 'classnames'
import { useMachine } from '@xstate/react'
import { Response } from 'request-dot-js'

import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UserClient, TokenVerificationResponse } from 'api/clients/UserClient'
import Shared from '../../Shared.module.scss'
import Styles from './EmailVerification.module.scss'
import emailVerificationMachine from './emailVerificationMachine'
import { useHistory, useLocation } from 'react-router-dom'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import RightPanelContent from '../RightPanelContent/RightPanelContent'
import { GhostButton } from 'components/Buttons/GhostButton'
import { ReactComponent as LeftArrowIcon } from 'assets/icons/arrowLeft24.svg'
import { Button } from 'components/Button/Button'
import { LOGIN_PATH, SIGNUP_PATH } from 'paths'
import { useIsBlaze } from 'hooks/useIsBlaze'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'

type Props = {
  token: string
  transitionPage: (page: string) => void
}

const EmailVerification = observer((props: Props) => {
  const { token, transitionPage } = props
  const [state, localSend] = useMachine(emailVerificationMachine)
  const history = useHistory()
  const location = useLocation()
  const { isTabletPortrait } = useResponsiveDesign()
  const isBlaze = useIsBlaze()
  const featureFlags = useRootStore().featureFlags

  const newRegistrationEnabled = useFlipperFlag('new_registration')

  useEffect(() => {
    if (!featureFlags.ready) {
      return
    }
    const showSuccessMessageAndSetEmail = (res: Response<TokenVerificationResponse>) => {
      const email = res.data.email

      const newRegistrationOverride = new URLSearchParams(location.search).get('new_registration_override')
      const isNewRegistration = newRegistrationEnabled || newRegistrationOverride === 'true'

      if (res.data.user_exists && !res.data.is_shell_user) {
        localSend('VERIFIED_AND_USER_EXISTS')
      } else {
        if (isNewRegistration) {
          history.replace(`${SIGNUP_PATH}?email=${encodeURIComponent(email)}&email_verified=true`)
        } else {
          transitionPage('CREATE_ACCOUNT')
          showToastMessage('Email Verified! 🎉', 'success')
        }
      }
    }

    const showFailureMessage = (res: Response<TokenVerificationResponse>) => {
      localSend('NOT_VERIFIED')
    }

    const verifyToken = async () => {
      const res = await UserClient.verifyRegistrationToken(token)

      if (res.type === SUCCESS_RESPONSE_TYPE && res.data.success) {
        showSuccessMessageAndSetEmail(res)
        return
      }

      showFailureMessage(res)
    }

    verifyToken()
  }, [localSend, transitionPage, token, history, location.search, newRegistrationEnabled, featureFlags.ready])

  const goBack = () => {
    const queryParams = new URLSearchParams(location.search)
    queryParams.delete('registration_verification_token')
    history.replace({ search: queryParams.toString() })
    transitionPage('GO_BACK')
  }

  const goToLogin = () => {
    const queryParams = new URLSearchParams(location.search)
    queryParams.delete('registration_verification_token')
    history.replace({ pathname: LOGIN_PATH, search: queryParams.toString() })
    transitionPage('GO_BACK')
  }

  return (
    <div data-testid="emailVerification" className={cx(Shared.content, { [Shared.isBlaze]: isBlaze })}>
      <div className={cx(Shared.leftPanel, Styles.leftPanel)}>
        <div className={Shared.header}>
          <GhostButton className={Shared.backButton} aria-label="Back" onClick={goBack}>
            <LeftArrowIcon />
          </GhostButton>
        </div>
        <div className={cx(Shared.leftContent, Styles.leftContent)}>
          {state.matches('isVerifying') && (
            <div className={cx(Shared.title, { [Shared.isBlaze]: isBlaze })}>Verifying...</div>
          )}
          {state.matches('isNotVerified') && (
            <>
              <div className={cx(Shared.title, Styles.title, { [Shared.isBlaze]: isBlaze })}>Oh no!</div>
              <div className={cx(Shared.subTitle, Styles.subTitle)}>
                <p>We encountered a problem...</p>
              </div>
              <div className={cx(Shared.footer, Styles.footer)}>
                <Button
                  className={cx(Shared.actionButton, Styles.continueButton, { [Shared.isBlaze]: isBlaze })}
                  variant="primary"
                  onPress={goBack}
                >
                  Try again.
                </Button>
              </div>
            </>
          )}
          {state.matches('isVerifiedWithUser') && (
            <>
              <div className={cx(Shared.title, Styles.title, { [Shared.isBlaze]: isBlaze })}>
                You already have an account!
              </div>
              <div className={cx(Shared.footer, Styles.footer)}>
                <Button
                  className={cx(Shared.actionButton, Styles.continueButton, { [Shared.isBlaze]: isBlaze })}
                  variant="primary"
                  onPress={goToLogin}
                >
                  Login <span className={Styles.rightArrow}>&#8594;</span>
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
      {!isTabletPortrait && <RightPanelContent screen="createAccount" />}
    </div>
  )
})

export default EmailVerification
